import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import PageMeta from "@/components/PageMeta";
import { ArrowRight, MapPin, Handshake, FileText, Scale, TrendingUp, Calculator } from "lucide-react";

const COPY = {
  de: {
    eyebrow: "Leistungen · 03",
    h1a: "Grundstücke,",
    h1b_em: "Ankauf",
    h1b: "& Kooperationen.",
    lede: "Wir suchen entwicklungsfähige Liegenschaften in Wien, Niederösterreich und ausgewählten Lagen — direkter Ankauf, Joint Ventures oder Kooperationsmodelle mit Eigentümern.",

    intro_title: "Fair, schnell, dokumentiert.",
    intro_body: "Eine erste Einschätzung erfolgt innerhalb weniger Werktage. Wir prüfen Lage, Widmung, Baurecht, Bestand und Erschließung und legen das Ergebnis transparent dar. Vertraulichkeit ist während der Prüfphase selbstverständlich.",

    seek_title: "Was wir suchen.",
    seek: [
      { icon: MapPin, no: "01", title: "Lage", text: "Wien, Wiener Umland, ausgewählte Landeshauptstädte. Anbindung, Mikrolage, Stadtteilqualität." },
      { icon: Calculator, no: "02", title: "Grundstücksgröße", text: "Ab ca. 400 m² für Wohnbau, ab ca. 800 m² für Mehrparteienprojekte." },
      { icon: FileText, no: "03", title: "Widmung", text: "Wohngebiet, gemischtes Baugebiet, Bauland — auch entwicklungs- bzw. umwidmungsfähig." },
      { icon: TrendingUp, no: "04", title: "Bestand & Entwicklung", text: "Unbebaute Grundstücke, Bestand zur Sanierung oder Abriss/Neubau, Dachausbau-Potenzial." },
    ],

    coop_title: "Kooperationsmodelle.",
    coops: [
      { title: "Direkter Ankauf", text: "Wir kaufen die Liegenschaft direkt. Notariell sauber, marktgerecht bewertet, zügige Abwicklung." },
      { title: "Joint Venture", text: "Eigentümer bringt das Grundstück ein, wir bringen Planung, Genehmigung, Bau und Vermarktung. Wertentwicklung gemeinsam." },
      { title: "Baurechtsmodell", text: "Eigentümer behält das Grundstück, wir entwickeln und betreiben auf Baurechtsbasis. Geeignet für langfristig orientierte Eigentümer." },
      { title: "Partnerschaft mit Eigentümern", text: "Beratung bei Sanierung, Aufstockung oder Umnutzung — vom Konzept bis zur Übergabe." },
    ],

    process_title: "Ablauf.",
    process: [
      { no: "01", title: "Anfrage", text: "Sie senden uns Lage, Größe, Widmung — gerne mit Grundbuchsauszug oder Lageplan." },
      { no: "02", title: "Erste Einschätzung", text: "Innerhalb von 5–10 Werktagen mit einer ersten Wirtschaftlichkeitsbetrachtung." },
      { no: "03", title: "Vor-Ort-Termin", text: "Begehung, technische Vorprüfung, persönliches Gespräch." },
      { no: "04", title: "Strukturierter Vorschlag", text: "Konkretes Modell, Preisrahmen oder Kooperationsstruktur, Zeitplan." },
      { no: "05", title: "Verträge & Abwicklung", text: "Notarielle Beurkundung, Übergabe an Planungsphase." },
    ],

    cta_title: "Grundstück oder Liegenschaft?",
    cta_body: "Senden Sie uns kurz Lage, Größe und Widmung — wir melden uns mit einer ersten Einschätzung. Vertraulichkeit garantiert.",
    cta_btn: "Grundstück anbieten",
  },
  en: {
    eyebrow: "Services · 03",
    h1a: "Land,",
    h1b_em: "Acquisition",
    h1b: "& Cooperations.",
    lede: "We are looking for developable properties in Vienna, Lower Austria, and selected locations — direct acquisition, joint ventures, or cooperation models with owners.",
    intro_title: "Fair, Fast, Documented.",
    intro_body: "An initial assessment is provided within a few business days. We examine the location, zoning, building rights, existing structures, and development access, presenting the results transparently. Confidentiality is a matter of course during the evaluation phase.",
    seek_title: "What We Seek.",
    seek: [
      { icon: MapPin, no: "01", title: "Location", text: "Vienna, Vienna surroundings, selected state capitals. Connectivity, micro-location, neighborhood quality." },
      { icon: Calculator, no: "02", title: "Plot Size", text: "From approx. 400 m² for residential construction, from approx. 800 m² for multi-party projects." },
      { icon: FileText, no: "03", title: "Zoning", text: "Residential area, mixed building area, building land — also developable or re-zoneable." },
      { icon: TrendingUp, no: "04", title: "Existing & Development", text: "Undeveloped plots, existing structures for rehabilitation or demolition/new construction, attic conversion potential." },
    ],
    coop_title: "Cooperation Models.",
    coops: [
      { title: "Direct Acquisition", text: "We acquire the property directly. Notarially sound, market-fair valuation, swift processing." },
      { title: "Joint Venture", text: "Owner contributes the land, we contribute planning, approval, construction, and marketing. Joint value development." },
      { title: "Building Rights Model", text: "Owner retains the land; we develop and operate on a building rights basis. Suitable for long-term oriented owners." },
      { title: "Partnership with Owners", text: "Consulting for rehabilitation, vertical extension, or repurposing — from concept to handover." },
    ],
    process_title: "Process.",
    process: [
      { no: "01", title: "Inquiry", text: "You send us location, size, zoning — preferably with land register extract or site plan." },
      { no: "02", title: "Initial Assessment", text: "Within 5–10 business days with a preliminary economic viability assessment." },
      { no: "03", title: "On-Site Visit", text: "Inspection, preliminary technical review, personal discussion." },
      { no: "04", title: "Structured Proposal", text: "Concrete model, price range or cooperation structure, timeline." },
      { no: "05", title: "Contracts & Execution", text: "Notarial certification, handover to planning phase." },
    ],
    cta_title: "Land or Property?",
    cta_body: "Briefly send us location, size, and zoning — we will get back to you with an initial assessment. Confidentiality guaranteed.",
    cta_btn: "Offer Land",
  },
  ru: {
    eyebrow: "Услуги · 03",
    h1a: "Земельные участки,",
    h1b_em: "приобретение",
    h1b: "& сотрудничество.",
    lede: "Мы ищем пригодные для застройки объекты в Вена, Нижней Австрии и отдельных регионах — прямой выкуп, совместные предприятия или модели сотрудничества с собственниками.",
    intro_title: "Честно, быстро, документально.",
    intro_body: "Первоначальная оценка проводится в течение нескольких рабочих дней. Мы проверяем местоположение, назначение, строительное право, существующие объекты и доступ к коммуникациям, прозрачно представляя результаты. Конфиденциальность на этапе проверки гарантируется.",
    seek_title: "Что мы ищем.",
    seek: [
      { icon: MapPin, no: "01", title: "Местоположение", text: "Вена, окрестности Вена, отдельные столицы земель. Транспортная доступность, микрорайон, качество района." },
      { icon: Calculator, no: "02", title: "Площадь участка", text: "От ок. 400 м² для жилой застройки, от ок. 800 м² для многоквартирных проектов." },
      { icon: FileText, no: "03", title: "Назначение", text: "Жилая зона, смешанная строительная зона, земля под застройку — также пригодные для развития или перепрофилирования." },
      { icon: TrendingUp, no: "04", title: "Существующая застройка и развитие", text: "Незастроенные участки, существующие объекты для санации или сноса/нового строительства, потенциал для мансардной застройки." },
    ],
    coop_title: "Модели сотрудничества.",
    coops: [
      { title: "Прямой выкуп", text: "Мы покупаем недвижимость напрямую. Нотариально чисто, по рыночной стоимости, быстрое оформление." },
      { title: "Совместное предприятие", text: "Собственник вносит участок, мы предоставляем планирование, согласование, строительство и маркетинг. Совместное развитие стоимости." },
      { title: "Модель строительного права", text: "Собственник сохраняет участок, мы разрабатываем и эксплуатируем его на основе строительного права. Подходит для собственников, ориентированных на долгосрочную перспективу." },
      { title: "Партнерство с собственниками", text: "Консультации по санации, надстройке или перепрофилированию — от концепции до сдачи." },
    ],
    process_title: "Процесс.",
    process: [
      { no: "01", title: "Запрос", text: "Вы присылаете нам местоположение, размер, назначение — желательно с выпиской из поземельной книги или планом участка." },
      { no: "02", title: "Первоначальная оценка", text: "В течение 5–10 рабочих дней с предварительной оценкой экономической эффективности." },
      { no: "03", title: "Выезд на объект", text: "Осмотр, предварительная техническая проверка, личная беседа." },
      { no: "04", title: "Структурированное предложение", text: "Конкретная модель, ценовой диапазон или структура сотрудничества, график." },
      { no: "05", title: "Договоры и оформление", text: "Нотариальное заверение, передача на этап планирования." },
    ],
    cta_title: "Земельный участок или недвижимость?",
    cta_body: "Кратко отправьте нам местоположение, размер и назначение — мы свяжемся с вами с первой оценкой. Конфиденциальность гарантирована.",
    cta_btn: "Предложить участок",
  },
  uk: {
    eyebrow: "Послуги · 03",
    h1a: "Земельні ділянки,",
    h1b_em: "придбання",
    h1b: "& співпраця.",
    lede: "Ми шукаємо об'єкти, придатні для забудови, у Відні, Нижній Австрії та окремих місцях — пряма купівля, спільні підприємства або моделі співпраці з власниками.",
    intro_title: "Чесно, швидко, задокументовано.",
    intro_body: "Первинна оцінка надається протягом кількох робочих днів. Ми перевіряємо місце розташування, цільове призначення, будівельне право, існуючі об'єкти та інфраструктуру, прозоро представляючи результати. Конфіденційність під час етапу перевірки є само собою зрозумілою.",
    seek_title: "Що ми шукаємо.",
    seek: [
      { icon: MapPin, no: "01", title: "Місце розташування", text: "Відень, околиці Відня, окремі столиці земель. Транспортна доступність, мікрорайон, якість району." },
      { icon: Calculator, no: "02", title: "Розмір ділянки", text: "Від прибл. 400 м² для житлової забудови, від прибл. 800 м² для багатоквартирних проектів." },
      { icon: FileText, no: "03", title: "Цільове призначення", text: "Житлова зона, змішана будівельна зона, земля під забудову — також придатні для розвитку або зміни цільового призначення." },
      { icon: TrendingUp, no: "04", title: "Існуючі об'єкти та розвиток", text: "Незабудовані ділянки, існуючі об'єкти для санації або знесення/нового будівництва, потенціал для мансардного поверху." },
    ],
    coop_title: "Моделі співпраці.",
    coops: [
      { title: "Пряма купівля", text: "Ми купуємо нерухомість безпосередньо. Нотаріально оформлено, оцінено за ринковою вартістю, швидке завершення." },
      { title: "Спільне підприємство", text: "Власник вносить земельну ділянку, ми надаємо планування, дозвіл, будівництво та маркетинг. Спільний розвиток вартості." },
      { title: "Модель будівельного права", text: "Власник зберігає земельну ділянку; ми розробляємо та експлуатуємо її на основі будівельного права. Підходить для власників, орієнтованих на довгострокову перспективу." },
      { title: "Партнерство з власниками", text: "Консультації щодо санації, надбудови або перепрофілювання — від концепції до передачі." },
    ],
    process_title: "Процес.",
    process: [
      { no: "01", title: "Запит", text: "Ви надсилаєте нам місце розташування, розмір, цільове призначення — бажано з витягом з поземельної книги або планом ділянки." },
      { no: "02", title: "Первинна оцінка", text: "Протягом 5–10 робочих днів з попередньою оцінкою економічної ефективності." },
      { no: "03", title: "Виїзд на об'єкт", text: "Огляд, попередня технічна перевірка, особиста розмова." },
      { no: "04", title: "Структурована пропозиція", text: "Конкретна модель, ціновий діапазон або структура співпраці, графік." },
      { no: "05", title: "Договори та оформлення", text: "Нотаріальне посвідчення, передача на етап планування." },
    ],
    cta_title: "Земельна ділянка або нерухомість?",
    cta_body: "Коротко надішліть нам місце розташування, розмір та цільове призначення — ми зв'яжемося з вами з першою оцінкою. Конфіденційність гарантована.",
    cta_btn: "Запропонувати ділянку",
  },
};

export default function GrundstueckePage() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.de;

  return (
    <div data-testid="grundstuecke-page">
      <PageMeta pageKey="grundstuecke" />

      <section className="relative pt-32 pb-24 md:pt-44 md:pb-32 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex items-center gap-4 mb-10">
          <span className="w-12 h-px bg-white/55" />
          <span className="text-[11px] tracking-[0.32em] uppercase text-white/65">{c.eyebrow}</span>
        </div>
        <h1 className="font-display font-light text-5xl sm:text-6xl md:text-7xl lg:text-8xl uppercase tracking-tighter leading-[0.95] break-words hyphens-auto">
          {c.h1a}
          <br />
          <span className="italic font-extralight">{c.h1b_em}</span> {c.h1b}
        </h1>
        <p className="mt-10 max-w-2xl text-base md:text-lg text-white/80 leading-[1.65] font-light">{c.lede}</p>
      </section>

      <section className="py-20 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 grid md:grid-cols-12 gap-10 lg:gap-20 border-t border-white/10">
        <div className="md:col-span-5 min-w-0">
          <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-5">01</p>
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] break-words hyphens-auto">
            {c.intro_title}
          </h2>
        </div>
        <div className="md:col-span-7 md:pt-8 text-white/80 text-base md:text-lg leading-[1.7] font-light">
          <p>{c.intro_body}</p>
        </div>
      </section>

      <section className="py-24 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 border-t border-white/10">
        <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-3">02</p>
        <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-16">
          {c.seek_title}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
          {c.seek.map((it) => (
            <div key={it.no} className="bg-[#051630] p-8 md:p-10">
              <div className="flex items-baseline justify-between mb-5">
                <it.icon size={22} strokeWidth={1.2} className="text-white/70" />
                <span className="text-[10px] tracking-[0.3em] uppercase text-white/40">{it.no}</span>
              </div>
              <h3 className="font-display text-lg uppercase tracking-tight mb-3">{it.title}</h3>
              <p className="text-sm text-white/70 leading-[1.7] font-light">{it.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 border-t border-white/10">
        <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-3">03</p>
        <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-16">
          {c.coop_title}
        </h2>
        <div className="grid sm:grid-cols-2 gap-px bg-white/10">
          {c.coops.map((t) => (
            <div key={t.title} className="bg-[#051630] p-8 md:p-10">
              <Handshake size={22} strokeWidth={1.2} className="text-white/60 mb-4" />
              <h3 className="font-display text-xl uppercase tracking-tight mb-3">{t.title}</h3>
              <p className="text-sm text-white/70 leading-[1.7] font-light">{t.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 border-t border-white/10">
        <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-3">04</p>
        <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-16">
          {c.process_title}
        </h2>
        <div className="space-y-px bg-white/10">
          {c.process.map((s) => (
            <div key={s.no} className="bg-[#051630] grid md:grid-cols-12 gap-6 px-6 md:px-10 py-8">
              <div className="md:col-span-2 text-[11px] tracking-[0.3em] uppercase text-white/45">{s.no}</div>
              <div className="md:col-span-3 font-display text-lg md:text-xl uppercase tracking-tight">{s.title}</div>
              <p className="md:col-span-7 text-white/75 leading-[1.7] font-light">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 border-t border-white/10 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7 min-w-0">
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] break-words hyphens-auto">
            {c.cta_title}
          </h2>
          <p className="mt-6 max-w-xl text-white/75 leading-[1.7] font-light">{c.cta_body}</p>
        </div>
        <div className="md:col-span-5 flex md:justify-end md:items-end">
          <Link
            to="/kontakt"
            data-testid="grundstuecke-cta"
            className="group inline-flex items-center gap-6 bg-white text-[#051630] font-semibold tracking-[0.2em] uppercase text-[11px] px-8 py-5 border border-white hover:bg-transparent hover:text-white transition-colors"
          >
            <span>{c.cta_btn}</span>
            <ArrowRight size={16} strokeWidth={2} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
